// Quick-login QR: turning the existing 6-digit family code into something a
// relative can just point a camera at.
//
// PAYLOAD CHOICE. The QR encodes a normal https link rather than a private
// scheme or a bare code. That means any phone's built-in camera can read it -
// no "find the scan button in an app you haven't installed yet", which is the
// whole point of the feature. The cost is that a photographed QR is a
// tappable link for as long as the code lives. That is the same exposure the
// app already accepts by texting the plain code, and the underlying
// protections are unchanged: the code is single-use, expires in 10 minutes,
// and both generating and redeeming are rate-limited server-side.
//
// The link lands on /join, which PRE-FILLS the code and waits for a tap. It
// deliberately does not log anyone in automatically - a stray scan should
// never silently hand someone an account.

import { publicOrigin } from "./publicOrigin";

export const CODE_LENGTH = 6;
export const JOIN_PATH = '/join';
// FALLBACK_ORIGIN lived here too until v18.34 - a second definition of a
// constant utils/publicOrigin.js exports, left behind when the origin logic
// moved out on 13 Sep 2026. Nothing read it. A shadow like that is invisible
// to a search for who EXPORTS a name and looks authoritative to the next
// reader, which is how the fork spent a month drawing one grey bar.

export function isValidCode(code) {
  const c = String(code ?? '').trim();
  return c.length === CODE_LENGTH && /^[0-9]+$/.test(c);
}

/**
 * Where the QR should point. Inside the installed app window.location.origin
 * is a capacitor:// or file:// URL that means nothing to anyone else's phone,
 * so a real public origin is required; a configured one wins, otherwise the
 * current origin if it is genuinely web, otherwise the production site.
 */
export function joinUrlFor(code, { configured, origin } = {}) {
  if (!isValidCode(code)) return '';
  // The origin logic moved to utils/publicOrigin.js so the list-share links
  // use the same rule. It was written here first and never carried across.
  // (13 Sep 2026)
  const base = publicOrigin({ configured, origin });
  return `${base}${JOIN_PATH}?code=${encodeURIComponent(String(code).trim())}`;
}

/**
 * Pull a code out of whatever was scanned or pasted: a join URL, a bare
 * 6-digit code, or a query string. Returns null when there is nothing valid -
 * never a partial guess.
 */
export function parseJoinCode(input) {
  const text = String(input ?? '').trim();
  if (!text) return null;
  if (isValidCode(text)) return text;

  const match = text.match(/[?&]code=([^&\s]+)/i);
  if (match) {
    const candidate = decodeURIComponent(match[1]).trim();
    return isValidCode(candidate) ? candidate : null;
  }
  return null;
}

/**
 * Render a zxing BitMatrix as an SVG path.
 *
 * Done here rather than with the library's DOM writer so the output can be
 * unit-tested and styled: one path of small squares scales cleanly and keeps
 * the DOM to a single node instead of several hundred rects.
 *
 * `matrix` only needs { getWidth(), getHeight(), get(x, y) }.
 */
export function matrixToSvgPath(matrix) {
  if (!matrix || typeof matrix.getWidth !== 'function') return { size: 0, path: '' };
  const w = matrix.getWidth();
  const h = matrix.getHeight();
  if (!w || !h) return { size: 0, path: '' };
  const parts = [];
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (matrix.get(x, y)) parts.push(`M${x} ${y}h1v1h-1z`);
    }
  }
  return { size: w, path: parts.join('') };
}

/**
 * The invite message the owner sends.
 *
 * The link matters more than the digits: tapping it beats typing six numbers
 * off another phone, especially for whoever in the family is least patient
 * with technology. The digits stay in the text as a fallback for anyone whose
 * messaging app strips links, and because the code alone still works in the
 * app's "I have a code" screen.
 */
export function buildInviteMessage(code, url) {
  if (!isValidCode(code)) return '';
  const link = url || '';
  const lines = [
    link ? `Join our Xelanti family list: ${link}` : 'Join our Xelanti family list.',
    `If the link doesn't work, open the app, tap "I have a code" and enter ${String(code).trim()}.`,
    'It works once and expires in 10 minutes.',
  ];
  return lines.join('\n\n');
}
